import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import ActionsContext from "common/ActionsContext";
import { csvName, safeStr } from "common/api";
import { expressLatencyTarget } from "../api";
import { intervalForHours } from "views/Statistics/DPI/api";
import { getIndexes } from "common/utils";
import Request from "common/Request";
import * as d3 from "d3";
import Switch from "@mui/material/Switch";
import DistributionPlot from "common/graphs/DistributionPlot";
import BqnContext from "common/BqnContext";

const Wrapper = styled.div`
  .subscriber-dashboard {
    margin: 0 20px;
  }
`;

const ChartTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 0;
`;

const SelectWrapper = styled.div`
  width: 50%;
  max-width: 200px;
  margin: 0 10px 10px 0;
`;

const SwitchWithLabel = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

const LayoutRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 650px) {
    flex-direction: column;
    align-items: flex-end;
    flex-wrap: wrap;

    .displaySelect {
      margin-right: 30px;
    }
  }
`;

const Label = styled.div`
  margin-right: 10px;
  font-family: AllianceNo2-SemiBold !important;
  font-weight: 100;
  color: var(--Text-Primary);
  width: 100px;
  text-align: right;
`;

const SwitchWrapper = styled.div`
  width: auto;
  display: flex;
  align-items: center;
  position: relative;
`;

const getCategoryFilterCmd = (category) => {
  if (category === "all-average") {
    return "all";
  } else {
    return `name "${safeStr(category).replaceAll("'", "")}"`;
  }
};

const getSubscriberFilterCmd = (subscriber, target) => {
  if (subscriber === "all-subs") {
    return "all";
  } else if (subscriber.includes("ip-access")) {
    return target.addr;
  } else {
    return `id '${target.subsId.replaceAll("\\", "\\\\").replaceAll("'", "\\'")}'`;
  }
};

const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%S");

export function parseTimeEvolution(response) {
  const [header, ...rows] = response.trim().split("\n");
  const headerIndexes = getIndexes(header);
  const parsedDate = rows.reduce(
    (acc, row) => {
      const attrs = row.trim().split(/\s+/);
      const itemDownlink = {
        time: new Date(parseDate(attrs[headerIndexes["TIME"]])),
        min: parseFloat(attrs[headerIndexes["D-MIN-MS"]]),
        p10: parseFloat(attrs[headerIndexes["D-10%-MS"]]),
        p25: parseFloat(attrs[headerIndexes["D-25%-MS"]]),
        p50: parseFloat(attrs[headerIndexes["D-50%-MS"]]),
        p75: parseFloat(attrs[headerIndexes["D-75%-MS"]]),
        p90: parseFloat(attrs[headerIndexes["D-90%-MS"]]),
        max: parseFloat(attrs[headerIndexes["D-MAX-MS"]]),
      };
      const itemUplink = {
        time: new Date(parseDate(attrs[headerIndexes["TIME"]])),
        min: parseFloat(attrs[headerIndexes["U-MIN-MS"]]),
        p10: parseFloat(attrs[headerIndexes["U-10%-MS"]]),
        p25: parseFloat(attrs[headerIndexes["U-25%-MS"]]),
        p50: parseFloat(attrs[headerIndexes["U-50%-MS"]]),
        p75: parseFloat(attrs[headerIndexes["U-75%-MS"]]),
        p90: parseFloat(attrs[headerIndexes["U-90%-MS"]]),
        max: parseFloat(attrs[headerIndexes["U-MAX-MS"]]),
      };
      acc.downlink.push(itemDownlink);
      acc.uplink.push(itemUplink);
      return acc;
    },
    { downlink: [], uplink: [] }
  );
  return parsedDate;
};

function getCmdSubscribergroup(group) {
  if (!group || group === "all-subscribers") return "all";
  return `name '${csvName(group)}'`;
}

const loadTimeEvolution = ({
  hours,
  target,
  destination,
  filter,
  dpiEnabled,
  interval,
  app,
  subscriberGroup,
  mode,
  bqnId=null,
}) => {
  return expressLatencyTarget(target ?? { addr: null, subsId: null })
    .then((expressedTarget) => {
      return ifCl.run(
        `show statistics dpi latency time-dist hours ${hours} interval ${intervalForHours(
          hours
        )} category ${getCategoryFilterCmd(
          app
        )} subscriber ${getSubscriberFilterCmd(
          expressedTarget,
          target
        )} policy all subscriber-group ${getCmdSubscribergroup(
          subscriberGroup
        )}${
          mode !== 'cluster' || bqnId === null || bqnId === 'all' ? '' : ` bqn-id ${bqnId}`
        }`
      );
    })
    .then((response) => parseTimeEvolution(response))
    .catch((error) => showModalError("Error:", error));
};

function LatencyAndTimeEvolution({
  latencies,
  hours,
  target,
  destination,
  filter,
  dpiEnabled,
  interval,
  subscriberGroup,
  className,
}) {
  const [selectedApp, setSelectedApp] = useState(
    latencies.length > 0 ? latencies[0].service : undefined
  );
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const [plotMode, setPlotMode] = useState("boxplot");
  const [showMinMax, setShowMinMax] = useState(false);
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);
  const doLoad = () => {
    if (!selectedApp) {
      setRequest(null);
      return;
    }
    setRequest(
      loadTimeEvolution({
        hours,
        target,
        destination,
        filter,
        dpiEnabled,
        interval,
        app: selectedApp,
        subscriberGroup,
        mode,
        bqnId,
      })
    );
  };

  useEffect(() => {
    doLoad();

    return actions.recv("do-load", doLoad);
  }, [
    JSON.stringify(latencies),
    hours,
    target,
    destination,
    filter,
    dpiEnabled,
    interval,
    selectedApp,
    subscriberGroup,
  ]);

  if (request === null) {
    return null;
  }

  return (
    <Wrapper>
      <ChartTitle>{`Time Evolution of Internet Latencies for ${selectedApp}`}</ChartTitle>
      <LayoutRow className="latency-charts-wrapper time-evolution">
        <SelectWrapper>
          <SelectInputWithIcon
            title="Application"
            name="application"
            icon="memory"
            selected={selectedApp}
            onChange={({ target }) => {
              setSelectedApp(target.value);
            }}
            options={latencies.map((latency) => {
              const { service } = latency;
              return { label: service, value: service };
            })}
          />
        </SelectWrapper>
        <SwitchWrapper>
          <SwitchWithLabel>
            <Label>Line chart view</Label>{" "}
            <Switch
              className="sw-input"
              checked={plotMode === "line"}
              onChange={() =>
                setPlotMode(plotMode === "boxplot" ? "line" : "boxplot")
              }
            />
          </SwitchWithLabel>
          <SwitchWithLabel>
            <Label>View max+min</Label>{" "}
            <Switch
              className="sw-input"
              checked={showMinMax}
              onChange={() => setShowMinMax(!showMinMax)}
            />
          </SwitchWithLabel>
        </SwitchWrapper>
      </LayoutRow>
      <Request during={request}>
        {(result) => {
          const data = result.uplink;
          return (
            <div className={className}>
              <DistributionPlot
                plotMode={plotMode}
                data={data}
                xField="time"
                formatXField={d3.timeFormat("%m/%d %H:%M")}
                yAxisUnit="milliseconds"
                minmax={showMinMax}
                margin={{ top: 10, right: 50, bottom: 70, left: 60 }}
              />
            </div>
          );
        }}
      </Request>
    </Wrapper>
  );
}

export default LatencyAndTimeEvolution;
